// client/src/theme/theme.js
import { createTheme } from '@mui/material/styles';

// Central design tokens for the whole app. Keeping this in one place is what
// makes dark mode possible without hunting down hardcoded hex colors.
//
// 5 brand colors (primary/secondary/success/warning/error - the roles
// already used all over the UI as Button/Chip/Alert colors) always have a
// value, set by a super_admin in Manage Stores. backgroundColor/textColor
// are a separate, optional override - most stores leave those unset and
// just follow the light/dark mode default below like before.
export const getAppTheme = (
  mode,
  primaryColor = '#3563E9',
  secondaryColor = '#DC3545',
  successColor = '#22A45D',
  warningColor = '#ED6C02',
  errorColor = '#D32F2F',
  backgroundColor = null,
  textColor = null
) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: secondaryColor,
      },
      success: {
        main: successColor,
      },
      warning: {
        main: warningColor,
      },
      error: {
        main: errorColor,
      },
      background: {
        default: backgroundColor || (mode === 'light' ? '#F6F7FB' : '#0F1115'),
        paper: mode === 'light' ? '#FFFFFF' : '#1A1D24',
      },
      ...(textColor ? { text: { primary: textColor } } : {}),
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'sans-serif',
      ].join(','),
      h1: { fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontWeight: 800, letterSpacing: '-0.02em' },
      h3: { fontWeight: 800, letterSpacing: '-0.02em' },
      h4: { fontWeight: 800, letterSpacing: '-0.015em' },
      h5: { fontWeight: 700, letterSpacing: '-0.015em' },
      h6: { fontWeight: 700, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 600, lineHeight: 1.35 },
      body1: { lineHeight: 1.6 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.005em' },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 999, paddingInline: 20 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, fontWeight: 600 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 16,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            transition: 'border-color 0.2s ease',
            '&:hover': {
              borderColor: theme.palette.primary.light,
            },
          }),
        },
      },
    },
  });
